import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { AuthStore } from '../stores/auth.store';
import { isAdministrationApiRequest, isSecuredCcfApiRequest } from '../utils/secured-api.util';

/** Maps specific HTTP status codes to their localized error message keys. */
const STATUS_KEY_MAP: Record<number, string> = {
  0: 'errors.network',
  400: 'errors.bad_request',
  401: 'errors.unauthorized',
  403: 'errors.forbidden',
  404: 'errors.not_found',
  408: 'errors.timeout',
  409: 'errors.conflict',
};

/** Resolves the localized message key for a given HTTP error. */
const errorKeyFor = (error: HttpErrorResponse): string => {
  if (STATUS_KEY_MAP[error.status]) {
    return STATUS_KEY_MAP[error.status];
  }
  return error.status >= 500 ? 'errors.server' : 'errors.unexpected';
};

/**
 * Centralises HTTP error handling for CCF endpoints. Any failed CCF request shows
 * a global, localized error notification so individual stores only need to reset
 * their loading state. Non-CCF requests (e.g. newsletter) keep their own custom
 * error handling.
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // A 401 on a secured request while logged in means the session expired or was revoked.
      if (error.status === 401 && isSecuredCcfApiRequest(req.url)) {
        const authStore = injector.get(AuthStore);
        if (authStore.isLoggedIn()) {
          const notificationService = injector.get(NotificationService);
          notificationService.showError('errors.unauthorized');
          authStore.expireSession();
          return throwError(() => error);
        }
      }

      if (isAdministrationApiRequest(req.url)) {
        const notificationService = injector.get(NotificationService);
        notificationService.showError(errorKeyFor(error));
      }
      return throwError(() => error);
    })
  );
};
